import { toUserMessage } from 'utils/apiErrorHelper';
import type { IFaceSyncAsyncCreateResponseProps } from 'services/Jobs/types';
import { api } from '../../DefaultService';
import { IStudentFilterProps } from '../../StudentService/types';
import { IPaginationParams, ISelectionOption } from '../../types';
import {
  IStudentRecognitionCreateProps,
  IStudentRecognitionProps,
  IStudentRecognitionSimpleProps,
  IStudentRecognitionUpdateParams,
} from './types';

const ENDPOINT = 'v1/recognitions/student';

const formatApiError = (error: any): Error => {
  return new Error(toUserMessage(error));
};

export const createStudentRecognitionAsync = async (
  data: IStudentRecognitionCreateProps,
): Promise<IStudentRecognitionProps> => {
  try {
    const response = await api.post(`${ENDPOINT}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
};

export const createStudentRecognitionAsyncJob = async (
  data: IStudentRecognitionCreateProps,
): Promise<IFaceSyncAsyncCreateResponseProps> => {
  try {
    const response = await api.post(`${ENDPOINT}/async`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
};

export const updateStudentRecognitionAsync = async ({
  id,
  data,
}: IStudentRecognitionUpdateParams): Promise<IStudentRecognitionProps> => {
  try {
    const response = await api.put(`${ENDPOINT}/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
};

export const updateStudentRecognitionAsyncJob = async ({
  id,
  data,
}: IStudentRecognitionUpdateParams): Promise<IFaceSyncAsyncCreateResponseProps> => {
  try {
    const response = await api.put(`${ENDPOINT}/${id}/async`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    });
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
};

export const deleteStudentRecognitionAsyncJob = async (
  id: number,
): Promise<IFaceSyncAsyncCreateResponseProps> => {
  try {
    const response = await api.delete(`${ENDPOINT}/${id}/async`);
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
};

export const getStudentRecognitionByIdAsync = async (
  id: number,
): Promise<IStudentRecognitionProps> => {
  return await api
    .get(`${ENDPOINT}/${id}`)
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getAllStudentsByLicenseAsync = async (
  pagination?: IPaginationParams,
  filter?: IStudentFilterProps,
): Promise<IStudentRecognitionSimpleProps[]> => {
  return await api
    .get(`${ENDPOINT}/getAllByLicense`, {
      params: { ...pagination, ...filter },
    })
    .then((response) => response.data)
    .catch((error) => {
      throw new Error(
        error?.response?.data?.userMessage || 'Erro interno do servidor!',
      );
    });
};

export const getAllStudentsForSelectionAsync = async (): Promise<ISelectionOption[]> => {
  try {
    const response = await api.get(`${ENDPOINT}/selection`);
    return response.data;
  } catch (error) {
    throw new Error('Erro ao buscar lista de estudantes.');
  }
};

export const setStudentTemporaryReleaseAsync = async ({
  id,
  minutes,
}: {
  id: number;
  minutes: number;
}): Promise<IStudentRecognitionProps> => {
  try {
    const response = await api.post(`${ENDPOINT}/${id}/temporary-release`, { minutes });
    return response.data;
  } catch (error) {
    throw formatApiError(error);
  }
};
